import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ApiError } from './api/client'
import { AuthProvider } from './auth/AuthContext'
import App from './App'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Auth and permission failures will not fix themselves; anything else
        // gets one more go in case it was a blip.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          return false
        }
        return failureCount < 1
      },
    },
  },
})

/**
 * Vite inlines VITE_API_BASE_URL at build time. If the Railway service was
 * built without it, every request would fall back to /api, which nginx answers
 * with index.html — producing a JSON parse error that says nothing about the
 * real cause. Fail loudly instead.
 */
const onLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname)
const misconfigured = !import.meta.env.VITE_API_BASE_URL && !onLocalhost

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {misconfigured ? (
      <div className="login-page">
        <div className="login-card">
          <div className="mark">Fulfil</div>
          <p className="tagline">Not configured</p>
          <div className="alert error">
            This build has no <code>VITE_API_BASE_URL</code>, so it does not know where the API
            lives. Set it as a service variable on this Railway service — the full backend URL with
            no trailing slash — and redeploy so the value is baked into the build.
          </div>
        </div>
      </div>
    ) : (
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    )}
  </StrictMode>,
)
